import { Dispatch, SetStateAction } from 'react';
import { styled } from 'styled-components';

import { Box, Flex } from '../Layout';
import { Text } from '../Text';
import { Button } from '../Button';
import { Assets } from '../../assets';
import { theme } from '../../styles';
import { useIsDeviceSize } from '../Media';

const MOBILE_SPACE = 15;

type PaginationProps = {
  totalItems: number;
  itemsPerPage: number;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
};

/**
 * Text and page navigation links to go in the bottom of the table
 */
export const Pagination = ({ totalItems, itemsPerPage, currentPage, setCurrentPage }: PaginationProps) => {
  return (
    <Flex justifyContent="space-between" flexDirection="row" alignItems="baseline" flexWrap="no-wrap" marginTop={20}>
      <Box>
        <PaginatedItemsShowingText totalItems={totalItems} itemsPerPage={itemsPerPage} currentPage={currentPage} />
      </Box>
      {totalItems > itemsPerPage && (
        <PageNavigation
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      )}
    </Flex>
  );
};

type PaginatedItemsShowingTextProps = Pick<PaginationProps, 'totalItems' | 'itemsPerPage' | 'currentPage'>;

/**
 * "Showing x-y of z items" text
 */
export const PaginatedItemsShowingText = ({
  totalItems,
  itemsPerPage,
  currentPage,
}: PaginatedItemsShowingTextProps) => (
  <Text color="black70" style={{ width: 'max-content' }} marginRight={[20, 0, 0]}>
    {`Visar ${(currentPage - 1) * itemsPerPage + 1}-${Math.min(
      currentPage * itemsPerPage,
      totalItems
    )} av ${totalItems}`}
  </Text>
);

const PageNavigation = ({ totalItems, itemsPerPage, currentPage, setCurrentPage }: PaginationProps) => {
  const lastPage = Math.ceil(totalItems / itemsPerPage);
  const pageNumbers = getPageNumbers(lastPage);
  const isMobile = useIsDeviceSize();
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === lastPage;

  const pageNumbersTrimmed = pageNumbers.slice(1, -1);
  let pageNumbersSlice = [];
  if (pageNumbers.slice(0, 5).includes(currentPage)) {
    // If current page is within first 5
    pageNumbersSlice = pageNumbersTrimmed.slice(0, 4);
  } else if (pageNumbers.slice(-5).includes(currentPage)) {
    // If current page is within last 5
    pageNumbersSlice = pageNumbersTrimmed.slice(-4);
  } else {
    const currentSlice = Math.floor(pageNumbersTrimmed.indexOf(currentPage) / 4);
    pageNumbersSlice = pageNumbersTrimmed.slice(currentSlice * 4, currentSlice * 4 + 4);
  }
  const pagesInSliceCount = pageNumbersSlice.length;

  return (
    <PageNavigationWrapper>
      <StyledButton
        styleAs="link"
        onClick={() => setCurrentPage(currentPage - 1)}
        disabled={isFirstPage}
        resetFlexProps
        ariaLabel="Föregående sida"
      >
        <Assets.ChevronIconOld
          variant="thin"
          direction="left"
          color={isFirstPage ? 'black50' : 'foraBlue'}
          marginRight={8}
        />
        <Text color={isFirstPage ? 'black50' : 'foraBlue'}>Föregående</Text>
      </StyledButton>
      {isMobile ? (
        <>
          <PageNumber pageNumber={1} currentPage={currentPage} setCurrentPage={setCurrentPage} />
          {pagesInSliceCount > 0 && pageNumbersSlice[0] !== 2 && <StyledText>...</StyledText>}
          {pageNumbersSlice.map(pageNumber => (
            <PageNumber
              pageNumber={pageNumber}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              key={`page-id-${pageNumber}`}
            />
          ))}
          {pagesInSliceCount > 0 && pageNumbersSlice[pagesInSliceCount - 1] !== lastPage - 1 && <ThreeDots />}
          <PageNumber pageNumber={lastPage} currentPage={currentPage} setCurrentPage={setCurrentPage} />
        </>
      ) : (
        <PageNumbers>
          <PageNumber pageNumber={1} currentPage={currentPage} setCurrentPage={setCurrentPage} />
          {pagesInSliceCount > 0 && pageNumbersSlice[0] !== 2 && <ThreeDots />}
          {pageNumbersSlice.map(pageNumber => (
            <PageNumber
              pageNumber={pageNumber}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              key={`page-id-${pageNumber}`}
            />
          ))}
          {pagesInSliceCount > 0 && pageNumbersSlice[pagesInSliceCount - 1] !== lastPage - 1 && <ThreeDots />}
          <PageNumber pageNumber={lastPage} currentPage={currentPage} setCurrentPage={setCurrentPage} />
        </PageNumbers>
      )}
      <StyledButton
        styleAs="link"
        onClick={() => setCurrentPage(currentPage + 1)}
        disabled={isLastPage}
        resetFlexProps
        style={{ marginRight: isMobile ? '-2px' : '0px' }}
        ariaLabel="Nästa sida"
      >
        <Text color={isLastPage ? 'black50' : 'foraBlue'}>Nästa</Text>
        <Assets.ChevronIconOld variant="thin" color={isLastPage ? 'black50' : 'foraBlue'} marginLeft={8} />
      </StyledButton>
    </PageNavigationWrapper>
  );
};

const PageNavigationWrapper = styled(Flex)`
  column-gap: 20px;
  justify-content: space-between;
  @media screen and (max-width: ${theme.breakpoint.md - 1}px) {
    width: 100%;
    flex-wrap: wrap;
    max-width: 220px;
    justify-content: flex-end;
    column-gap: 0px;
    margin-right: -${MOBILE_SPACE}px;
  }
`;

const PageNumbers = styled(Flex)`
  column-gap: 20px;
  width: 100%;
`;

const StyledButton = styled(Button)`
  @media screen and (max-width: ${theme.breakpoint.md - 1}px) {
    padding: 10px ${MOBILE_SPACE}px;
    align-self: flex-end;
  }
`;
const StyledText = styled(Text)`
  @media screen and (max-width: ${theme.breakpoint.md - 1}px) {
    padding: 10px 15px;
  }
`;

const PageNumber = ({
  pageNumber,
  currentPage,
  setCurrentPage,
}: {
  pageNumber: number;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
}) =>
  pageNumber === currentPage ? (
    <StyledText fontWeight="semibold" aria-current="page">
      {pageNumber}
    </StyledText>
  ) : (
    <StyledButton
      styleAs="link"
      onClick={() => setCurrentPage(pageNumber)}
      resetFlexProps
      ariaLabel={'sida ' + pageNumber}
    >
      {pageNumber}
    </StyledButton>
  );

const getPageNumbers = (totalPages: number) => {
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }
  return pageNumbers;
};

const ThreeDots = () => {
  return <NoInteractionText ariaHidden={true}>...</NoInteractionText>;
};

const NoInteractionText = styled(Text)`
  user-select: none;
  pointer-events: none;
  @media screen and (max-width: ${theme.breakpoint.md - 1}px) {
    padding: 10px 15px;
  }
`;
